import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';

const inputStyle = {
  width: '100%', maxWidth: 420, padding: '10px 12px', marginBottom: 14,
  border: '1px solid var(--vela-glass-border-bright)', borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
  background: 'var(--vela-glass-bg)', color: 'var(--vela-text)',
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 5, color: 'var(--vela-text-mid)' };

function Field({ label, hint, error, ...props }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input
        style={error ? { ...inputStyle, border: '1px solid #f87171', background: 'rgba(248,113,113,0.08)' } : inputStyle}
        {...props}
      />
      {error ? (
        <div style={{ fontSize: 12, color: '#f87171', marginTop: -10, marginBottom: 14 }}>{error}</div>
      ) : hint ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -10, marginBottom: 14 }}>{hint}</div>
      ) : null}
    </div>
  );
}

// Fields belonging to each step — also doubles as "what Next requires at
// least one of" (see next() below). Step 3 (the embed snippet) has none —
// there's nothing to fill in, so Next always just advances.
const STEP_FIELDS = {
  0: ['sendgrid_key_input'],
  1: ['twilio_sid_input', 'twilio_token_input', 'twilio_from'],
  2: ['hubspot_token_input', 'zapier_webhook'],
  3: [],
  4: ['reply_to_email', 'business_address', 'booking_link', 'lead_magnet_link'],
};
const UNFILLED_MSG = 'Unfilled — fill this in, or tap "Skip for now" instead.';

export default function Setup() {
  const { account, setAccount } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    sendgrid_key_input: '',
    twilio_sid_input: '', twilio_token_input: '', twilio_from: '',
    hubspot_token_input: '', zapier_webhook: '',
    reply_to_email: '', business_address: '', booking_link: '', lead_magnet_link: '',
  });
  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => (e[k] ? { ...e, [k]: null } : e));
  };

  const steps = ['Email', 'SMS', 'CRM', 'Website', 'Details'];

  const save = async (fields) => {
    const body = {};
    for (const k of fields) if (form[k]) body[k] = form[k];
    if (!Object.keys(body).length) return;
    setSaving(true);
    try {
      await axios.patch('/api/accounts/me', body);
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    const { data } = await axios.post('/api/accounts/complete-onboarding');
    setAccount(a => ({ ...a, onboarding_completed_at: new Date().toISOString() }));
    navigate('/dashboard');
    return data;
  };

  const next = async () => {
    const fields = STEP_FIELDS[step];
    // Next means "I filled this in" — require at least one field on this
    // step to actually have something in it, otherwise flag it instead of
    // silently behaving exactly like Skip. (Fields on a step can be
    // either/or — e.g. HubSpot token OR Zapier webhook — so "at least one"
    // rather than "all" is the right bar here, not "every field required.")
    if (fields.length && !fields.some(k => form[k].trim())) {
      setErrors(Object.fromEntries(fields.map(k => [k, UNFILLED_MSG])));
      return;
    }
    setErrors({});
    await save(fields);
    if (step < steps.length - 1) setStep(s => s + 1);
    else await finish();
  };

  const skip = async () => {
    setErrors({});
    if (step < steps.length - 1) setStep(s => s + 1);
    else await finish();
  };

  const embedSnippet = `<script src="${process.env.REACT_APP_API_URL || 'https://api.velaleads.ca'}/embed.js" data-api-key="${account?.api_key || 'YOUR_API_KEY'}" async></script>`;

  const [copied, setCopied] = useState(false);
  const copyEmbed = () => {
    navigator.clipboard.writeText(embedSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--vela-bg)', padding: 24,
    }}>
      <div style={{
        background: 'var(--vela-glass-bg)', backdropFilter: 'blur(24px) saturate(160%)',
        WebkitBackdropFilter: 'blur(24px) saturate(160%)', border: '1px solid var(--vela-glass-border)',
        borderRadius: 20, padding: '40px 40px', width: 520, maxWidth: '100%',
      }}>

        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          {steps.map((s, i) => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= step ? 'var(--primary)' : 'var(--border)',
            }} />
          ))}
        </div>

        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Step {step + 1} of {steps.length} — {steps[step]}
        </p>

        {step === 0 && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Connect your email sending</h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              Nothing sends — no welcome emails, no nurture sequences — until this is set up.
              Get your API key from sendgrid.com → Settings → API Keys.
            </p>
            <Field label="SendGrid API Key" type="text" placeholder="SG.xxxxxxxx"
              value={form.sendgrid_key_input} onChange={e => set('sendgrid_key_input', e.target.value)}
              error={errors.sendgrid_key_input} />
          </>
        )}

        {step === 1 && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Connect SMS (optional)</h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              If you want text-message follow-up alongside email, connect Twilio here. Skip this
              if you only want email — you can always add it later in Settings.
            </p>
            <Field label="Twilio Account SID" value={form.twilio_sid_input} onChange={e => set('twilio_sid_input', e.target.value)}
              error={errors.twilio_sid_input} />
            <Field label="Twilio Auth Token" type="password" value={form.twilio_token_input} onChange={e => set('twilio_token_input', e.target.value)}
              error={errors.twilio_token_input} />
            <Field label="Twilio From Number" placeholder="+19025551234" value={form.twilio_from} onChange={e => set('twilio_from', e.target.value)}
              error={errors.twilio_from} />
          </>
        )}

        {step === 2 && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Connect your CRM (optional)</h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              When a lead becomes sales-ready, Vela can hand them off automatically with full
              context. Use HubSpot if that's what you use, or Zapier to connect almost anything
              else. Skip this and sales-ready leads still show up right in your Vela dashboard.
            </p>
            <Field label="HubSpot Private App Token" type="password"
              hint="HubSpot → Settings → Integrations → Private Apps"
              value={form.hubspot_token_input} onChange={e => set('hubspot_token_input', e.target.value)}
              error={errors.hubspot_token_input} />
            <Field label="Zapier Webhook URL" placeholder="https://hooks.zapier.com/hooks/catch/..."
              hint="Use this if you don't use HubSpot — connects to almost any other CRM"
              value={form.zapier_webhook} onChange={e => set('zapier_webhook', e.target.value)}
              error={errors.zapier_webhook} />
          </>
        )}

        {step === 3 && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Add the capture form to your website</h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              Paste this snippet on your website, right before <code>&lt;/body&gt;</code>. This is
              how leads actually reach Vela — without it, nothing gets captured.
            </p>
            <code style={{
              display: 'block', background: 'rgba(0,0,0,0.3)', color: '#A6E3D0',
              padding: 16, borderRadius: 8, fontSize: 12, lineHeight: 1.6,
              overflowX: 'auto', whiteSpace: 'pre-wrap', marginBottom: 10,
            }}>{embedSnippet}</code>
            <button type="button" onClick={copyEmbed} style={{
              background: 'none', border: '1px solid var(--border)', borderRadius: 6,
              padding: '6px 14px', cursor: 'pointer', fontSize: 13, marginBottom: 8, color: 'var(--vela-text)',
            }}>{copied ? 'Copied!' : 'Copy snippet'}</button>
          </>
        )}

        {step === 4 && (
          <>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>A few more details</h1>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
              These personalize every email Vela sends on your behalf.
            </p>
            <Field label="Reply-To Email" placeholder="sarah@halifaxmortgage.ca"
              hint="Replies from leads land directly in this inbox"
              value={form.reply_to_email} onChange={e => set('reply_to_email', e.target.value)}
              error={errors.reply_to_email} />
            <Field label="Business Address" placeholder="Halifax, Nova Scotia, Canada"
              hint="Used in email footers for CASL compliance"
              value={form.business_address} onChange={e => set('business_address', e.target.value)}
              error={errors.business_address} />
            <Field label="Booking Link" placeholder="https://calendly.com/yourlink"
              value={form.booking_link} onChange={e => set('booking_link', e.target.value)}
              error={errors.booking_link} />
            <Field label="Lead Magnet Link" placeholder="https://yoursite.ca/guide.pdf"
              hint="Delivered in the Day 1 welcome email"
              value={form.lead_magnet_link} onChange={e => set('lead_magnet_link', e.target.value)}
              error={errors.lead_magnet_link} />
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
          <button type="button" onClick={skip} disabled={saving} style={{
            background: 'none', border: 'none', color: 'var(--text-muted)',
            cursor: 'pointer', fontSize: 13,
          }}>Skip for now</button>

          <button
            type="button"
            disabled={saving}
            onClick={next}
            style={{
              background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', borderRadius: 8,
              padding: '10px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : step === steps.length - 1 ? 'Finish setup' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
